import { useCallback, useEffect, useState, type ReactNode } from "react";
import { NavLink } from "react-router";

import { AccessGate } from "@/features/shared/AccessGate";
import { apiJson } from "@/lib/api";
import { cn } from "@/lib/utils";

type HealthResponse = {
  ok: boolean;
  keys: { openrouter: boolean; fal: boolean };
  access: { required: boolean; authorized: boolean };
};

const navLinkClass = ({ isActive }: { isActive: boolean }) =>
  cn(
    "flex h-16 items-center gap-2 border-b-2 px-4 font-mono text-xs uppercase tracking-[0.8px] transition-colors",
    isActive
      ? "border-primary text-foreground"
      : "border-transparent text-muted-foreground hover:text-foreground"
  );

const KeyStatus = ({ label, present }: { label: string; present: boolean }) => (
  <span className="hidden items-center gap-2 font-mono text-[11px] uppercase tracking-[0.8px] text-subtle sm:flex">
    <span
      className={cn(
        "h-2 w-2 rounded-full",
        present
          ? "animate-pulse bg-success shadow-[0_0_8px_rgba(34,197,94,0.6)]"
          : "bg-primary shadow-[0_0_8px_rgba(231,55,90,0.4)]"
      )}
    />
    {label}
  </span>
);

export const AppShell = ({ children }: { children: ReactNode }) => {
  const [health, setHealth] = useState<HealthResponse | null>(null);

  const refreshHealth = useCallback(() => {
    apiJson<HealthResponse>("/api/health").then(setHealth).catch(() => null);
  }, []);

  useEffect(() => {
    refreshHealth();
  }, [refreshHealth]);

  const locked = health?.access.required === true && !health.access.authorized;

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-10 border-b bg-background/80 backdrop-blur-md">
        <div className="mx-auto flex h-16 max-w-[1584px] items-center justify-between gap-6 px-6">
          <div className="flex items-center gap-3">
            <img
              src="/assets/logo-mark.svg"
              alt="WE AS WEB"
              className="h-6 w-auto"
            />
            <span>
              <span className="block text-sm font-semibold leading-tight">
                Workshop Playground
              </span>
              <span className="block font-mono text-[10px] uppercase tracking-[0.8px] text-subtle">
                We as web — model arena
              </span>
            </span>
          </div>

          <nav className="flex items-stretch self-stretch">
            <NavLink to="/" end className={navLinkClass}>
              <span className="text-primary">[01]</span>
              Text
            </NavLink>
            <NavLink to="/images" className={navLinkClass}>
              <span className="text-primary">[02]</span>
              Images
            </NavLink>
          </nav>

          <div className="flex items-center gap-5">
            <KeyStatus label="OpenRouter" present={health?.keys.openrouter ?? false} />
            <KeyStatus label="fal.ai" present={health?.keys.fal ?? false} />
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-[1584px] px-6 py-8">
        {locked ? <AccessGate onUnlocked={refreshHealth} /> : children}
      </main>
    </div>
  );
};
